import { useState, useCallback, useMemo } from 'react';
import { UseJudgeScoringReturn, ScoreData } from '../types/judge';

/**
 * Custom hook for managing judge scoring functionality
 * Handles score updates, comments, submission state, and dirty checking
 */
export const useJudgeScoring = (): UseJudgeScoringReturn => {
  const [scores, setScores] = useState<ScoreData>({});
  const [comments, setComments] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);

  // Check if there are unsaved changes
  const isDirty = useMemo(() => {
    const hasScores = Object.values(scores).some(score => score > 0);
    const hasComments = comments.trim().length > 0;
    
    return (hasScores || hasComments) && !isSubmitted;
  }, [scores, comments, isSubmitted]);

  // Update scores
  const updateScore = useCallback((newScores: ScoreData, isSubmit: boolean = false) => {
    setScores(newScores);
    setLastUpdateTime(new Date());
    
    if (isSubmit) {
      setIsSubmitted(true);
    } else if (isSubmitted) {
      // If scores were previously submitted but have changed, reset the submission state
      setIsSubmitted(false);
    }
  }, [isSubmitted]);

  // Update comments
  const updateComments = useCallback((newComments: string) => {
    setComments(newComments);
    setLastUpdateTime(new Date());
    
    // If comments change after submission, mark as not submitted
    if (isSubmitted) {
      setIsSubmitted(false);
    }
  }, [isSubmitted]);

  // Submit scores
  const submitScores = useCallback((finalScores: ScoreData, finalComments: string) => {
    setScores(finalScores);
    setComments(finalComments);
    setIsSubmitted(true);
    setLastUpdateTime(new Date());
  }, []);

  // Reset all scoring state
  const resetScoring = useCallback(() => {
    setScores({});
    setComments('');
    setIsSubmitted(false);
    setLastUpdateTime(null);
  }, []);

  // Load existing scores (for when switching routines)
  const loadExistingScores = useCallback((existingScores: ScoreData, existingComments: string, submitted: boolean, timestamp?: string) => {
    setScores(existingScores);
    setComments(existingComments);
    setIsSubmitted(submitted);
    setLastUpdateTime(timestamp ? new Date(timestamp) : new Date());
  }, []);

  return {
    scores,
    comments,
    isSubmitted,
    lastUpdateTime,
    updateScore,
    updateComments,
    submitScores,
    resetScoring,
    isDirty,
    loadExistingScores
  };
};








